import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Plus, Trash2, DollarSign, PiggyBank, Users, 
  TrendingUp, Calculator, Pencil, ChevronLeft, ChevronRight,
  Calendar, RefreshCw
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { FileText, Calendar as CalendarIcon, BarChart3 } from "lucide-react";
import DetalhamentoPage from "./DetalhamentoPage";
import RelatoriosPage from "./RelatoriosPage";

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

const entrySchema = z.object({
  valor_recebido: z.coerce.number().min(0, "Valor deve ser positivo"),
  valor_investido: z.coerce.number().min(0, "Valor deve ser positivo"),
  quantidade_clientes: z.coerce.number().int().min(0, "Valor deve ser positivo"),
  mes: z.coerce.number().int().min(1).max(12),
  ano: z.coerce.number().int().min(2020).max(2050),
});

function LancamentosContent() {
  const { authAxios } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const now = new Date();
  const mesAtualReal = now.getMonth() + 1;
  const anoAtualReal = now.getFullYear();
  
  // Estado para mês/ano selecionado (navegação)
  const [mesSelecionado, setMesSelecionado] = useState(mesAtualReal);
  const [anoSelecionado, setAnoSelecionado] = useState(anoAtualReal);
  
  // Lista de anos disponíveis (5 anos para trás e 5 para frente)
  const anos = Array.from({ length: 11 }, (_, i) => anoAtualReal - 5 + i);

  const form = useForm({
    resolver: zodResolver(entrySchema),
    defaultValues: {
      valor_recebido: 0,
      valor_investido: 0,
      quantidade_clientes: 0,
      mes: mesSelecionado,
      ano: anoSelecionado,
    },
  });

  const valorRecebido = form.watch("valor_recebido") || 0;
  const valorInvestido = form.watch("valor_investido") || 0;
  const lucroCalculado = valorRecebido - valorInvestido;

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get(`/entries?mes=${mesSelecionado}&ano=${anoSelecionado}`);
      setEntries(res.data);
    } catch (err) {
      toast.error("Erro ao carregar lançamentos");
    } finally {
      setLoading(false);
    }
  }, [authAxios, mesSelecionado, anoSelecionado]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Navegar para mês anterior
  const handleMesAnterior = () => {
    if (mesSelecionado === 1) {
      setMesSelecionado(12);
      setAnoSelecionado(anoSelecionado - 1);
    } else {
      setMesSelecionado(mesSelecionado - 1);
    }
    setCurrentPage(1);
  };

  // Navegar para próximo mês
  const handleMesProximo = () => {
    if (mesSelecionado === 12) {
      setMesSelecionado(1);
      setAnoSelecionado(anoSelecionado + 1);
    } else {
      setMesSelecionado(mesSelecionado + 1);
    }
    setCurrentPage(1);
  };

  // Voltar para mês atual
  const handleVoltarMesAtual = () => {
    setMesSelecionado(mesAtualReal);
    setAnoSelecionado(anoAtualReal);
    setCurrentPage(1);
  };

  // Verificar se está no mês atual
  const isCurrentMonth = mesSelecionado === mesAtualReal && anoSelecionado === anoAtualReal;

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      form.reset({
        valor_recebido: entry.valor_recebido,
        valor_investido: entry.valor_investido,
        quantidade_clientes: entry.quantidade_clientes,
        mes: entry.mes,
        ano: entry.ano,
      });
    } else {
      setEditingEntry(null);
      form.reset({
        valor_recebido: 0,
        valor_investido: 0,
        quantidade_clientes: 0,
        mes: mesSelecionado,
        ano: anoSelecionado,
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingEntry) {
        await authAxios.put(`/entries/${editingEntry.id}`, data);
        toast.success("Lançamento atualizado com sucesso!");
      } else {
        await authAxios.post("/entries", data);
        toast.success("Lançamento criado com sucesso!");
      }
      form.reset();
      setDialogOpen(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (err) {
      toast.error("Erro ao salvar lançamento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este lançamento?")) return;
    try {
      await authAxios.delete(`/entries/${id}`);
      toast.success("Lançamento excluído!");
      fetchEntries();
    } catch (err) {
      toast.error("Erro ao excluir lançamento");
    }
  };

  // Totals
  const totalRecebido = entries.reduce((acc, e) => acc + e.valor_recebido, 0);
  const totalInvestido = entries.reduce((acc, e) => acc + e.valor_investido, 0);
  const totalLucro = entries.reduce((acc, e) => acc + e.lucro, 0);
  const totalClientes = entries.reduce((acc, e) => acc + e.quantidade_clientes, 0);

  // Pagination calculations
  const totalItems = entries.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEntries = entries.slice(startIndex, endIndex);

  // Reset to page 1 when items per page changes
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="lancamentos-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Lançamentos</h2>
          <p className="text-muted-foreground">
            Gerencie seus lançamentos financeiros
          </p>
        </div>
        
        <Button onClick={() => handleOpenDialog()} data-testid="new-entry-btn">
          <Plus className="w-4 h-4 mr-2" />
          Novo Lançamento
        </Button>
      </div>

      {/* Navegação de Mês/Ano */}
      <Card className="border-2 border-primary/30 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-primary" />
              <div>
                <p className="text-sm font-bold text-primary">PERÍODO SELECIONADO</p>
                <p className="text-xs text-muted-foreground">Navegue entre os meses para visualizar ou editar</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleMesAnterior}
                data-testid="mes-anterior-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Select 
                  value={String(mesSelecionado)} 
                  onValueChange={(v) => { setMesSelecionado(Number(v)); setCurrentPage(1); }}
                >
                  <SelectTrigger className="w-32" data-testid="select-mes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((mes, index) => (
                      <SelectItem key={index} value={String(index + 1)}>{mes}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select 
                  value={String(anoSelecionado)} 
                  onValueChange={(v) => { setAnoSelecionado(Number(v)); setCurrentPage(1); }}
                >
                  <SelectTrigger className="w-24" data-testid="select-ano">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anos.map((ano) => (
                      <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleMesProximo}
                data-testid="mes-proximo-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              {!isCurrentMonth && (
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={handleVoltarMesAtual}
                  className="ml-2"
                  data-testid="voltar-mes-atual-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Mês Atual
                </Button>
              )}
            </div>
          </div>
          
          {!isCurrentMonth && (
            <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-sm text-amber-600 text-center">
                📅 Visualizando <strong>{MESES[mesSelecionado - 1]} de {anoSelecionado}</strong> - Você pode editar lançamentos deste período
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Recebido</p>
                <p className="text-lg font-bold number-display">{formatCurrency(totalRecebido)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <PiggyBank className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Investido</p>
                <p className="text-lg font-bold number-display">{formatCurrency(totalInvestido)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <TrendingUp className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Lucro</p>
                <p className={`text-lg font-bold number-display ${totalLucro >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {formatCurrency(totalLucro)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Users className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Clientes</p>
                <p className="text-lg font-bold number-display">{totalClientes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Lançamentos do Mês</CardTitle>
              <CardDescription>
                {entries.length} lançamento(s) em {MESES[mesSelecionado - 1]} de {anoSelecionado}
              </CardDescription>
            </div>
            
            {/* Items per page selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Exibir:</span>
              <Select
                value={String(itemsPerPage)}
                onValueChange={handleItemsPerPageChange}
              >
                <SelectTrigger className="w-20" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">por página</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>Nenhum lançamento neste mês</p>
              <p className="text-sm mt-2">Clique em &quot;Novo Lançamento&quot; para adicionar</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Mês/Ano</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Data Criação</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Recebido</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Investido</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Lucro</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Clientes</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedEntries.map((entry) => (
                      <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4 text-sm font-medium">
                          {MESES[entry.mes - 1]}/{entry.ano}
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {new Date(entry.created_at).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit"
                          })}
                        </td>
                        <td className="py-3 px-4 text-sm text-right number-display text-emerald-500">
                          {formatCurrency(entry.valor_recebido)}
                        </td>
                        <td className="py-3 px-4 text-sm text-right number-display text-blue-500">
                          {formatCurrency(entry.valor_investido)}
                        </td>
                        <td className={`py-3 px-4 text-sm text-right number-display ${entry.lucro >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                          {formatCurrency(entry.lucro)}
                        </td>
                        <td className="py-3 px-4 text-sm text-right">
                          {entry.quantidade_clientes}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenDialog(entry)}
                              data-testid={`edit-entry-${entry.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => handleDelete(entry.id)}
                              data-testid={`delete-entry-${entry.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Exibindo {startIndex + 1} - {Math.min(endIndex, totalItems)} de {totalItems} lançamentos
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      data-testid="pagination-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            className="w-8 h-8 p-0"
                            onClick={() => setCurrentPage(pageNum)}
                            data-testid={`pagination-page-${pageNum}`}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="pagination-next"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Entry Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEntry ? "Editar Lançamento" : "Novo Lançamento"}</DialogTitle>
            <DialogDescription>
              Preencha os dados do lançamento
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Seletores de Mês e Ano */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mês *</Label>
                <Select 
                  value={String(form.watch("mes"))} 
                  onValueChange={(v) => form.setValue("mes", Number(v))}
                >
                  <SelectTrigger data-testid="select-mes">
                    <SelectValue placeholder="Selecione o mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {MESES.map((mes, index) => (
                      <SelectItem key={index} value={String(index + 1)}>{mes}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ano *</Label>
                <Select 
                  value={String(form.watch("ano"))} 
                  onValueChange={(v) => form.setValue("ano", Number(v))}
                >
                  <SelectTrigger data-testid="select-ano">
                    <SelectValue placeholder="Selecione o ano" />
                  </SelectTrigger>
                  <SelectContent>
                    {anos.map((ano) => (
                      <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_recebido">Valor Recebido (R$)</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="valor_recebido"
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-10"
                  placeholder="0,00"
                  data-testid="input-valor-recebido"
                  {...form.register("valor_recebido")}
                />
              </div>
              {form.formState.errors.valor_recebido && (
                <p className="text-sm text-destructive">{form.formState.errors.valor_recebido.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor_investido">Valor Investido (R$)</Label>
              <div className="relative">
                <PiggyBank className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="valor_investido"
                  type="number"
                  step="0.01"
                  min="0"
                  className="pl-10"
                  placeholder="0,00"
                  data-testid="input-valor-investido"
                  {...form.register("valor_investido")}
                />
              </div>
              {form.formState.errors.valor_investido && (
                <p className="text-sm text-destructive">{form.formState.errors.valor_investido.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="quantidade_clientes">Quantidade de Clientes</Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="quantidade_clientes"
                  type="number"
                  min="0"
                  className="pl-10"
                  placeholder="0"
                  data-testid="input-quantidade-clientes"
                  {...form.register("quantidade_clientes")}
                />
              </div>
              {form.formState.errors.quantidade_clientes && (
                <p className="text-sm text-destructive">{form.formState.errors.quantidade_clientes.message}</p>
              )}
            </div>

            {/* Calculated profit */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Lucro Calculado</span>
                </div>
                <span className={`font-bold number-display ${lucroCalculado >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                  {formatCurrency(lucroCalculado)}
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                data-testid="submit-entry-btn"
              >
                {submitting ? "Salvando..." : editingEntry ? "Atualizar" : "Salvar Lançamento"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


export default function LancamentosPage() {
  return (
    <div className="space-y-6" data-testid="lancamentos-page">
      <Tabs defaultValue="lancamentos" className="w-full">
        <TabsList className="w-full grid grid-cols-3 mb-4" data-testid="lancamentos-tabs">
          <TabsTrigger value="lancamentos" className="gap-1.5" data-testid="tab-lancamentos">
            <FileText className="w-4 h-4" /> Lancamentos
          </TabsTrigger>
          <TabsTrigger value="detalhamento" className="gap-1.5" data-testid="tab-detalhamento">
            <CalendarIcon className="w-4 h-4" /> Detalhamento
          </TabsTrigger>
          <TabsTrigger value="relatorios" className="gap-1.5" data-testid="tab-relatorios">
            <BarChart3 className="w-4 h-4" /> Relatorios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="lancamentos">
          <LancamentosContent />
        </TabsContent>

        <TabsContent value="detalhamento">
          <DetalhamentoPage />
        </TabsContent>

        <TabsContent value="relatorios">
          <RelatoriosPage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
